'use client'

import { usePathname } from 'next/navigation'
import { useEffect } from 'react'

import { buildLocalizedPath, stripPathForLocaleSwitch } from '@/modules/languages/lib/localizedPath'
import type { PublicLanguageOption } from './LanguageSwitcher.client'

const COOKIE_NAME = 'lang_pref'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

/**
 * Bots de buscador NÃO devem ser redirecionados — eles têm de rastrear cada URL (/, /en, /es) como
 * ela é, senão o Google se confunde sobre qual URL é qual idioma. Combinado com hreflang/canonical
 * (já no <head>), isso mantém PT(BR)/EN/ES indexados corretamente. Suprimir o redirect p/ o bot NÃO
 * é cloaking (o conteúdo servido é o mesmo; só não "chutamos" o bot p/ outra URL).
 */
const BOT_UA_RE =
  // `lighthouse|page speed` (19/08/2026): o robô do PageSpeed Insights (UA «Chrome-Lighthouse») NÃO
  // casava com nenhum termo e era redirecionado → o laboratório media a página DUAS vezes (LCP de
  // 35 s era artefato disso, não experiência real). Suprimir para ferramenta de medição não é
  // cloaking — mesma justificativa dos bots acima.
  /bot|crawl|spider|slurp|mediapartners|google-inspectiontool|googleother|bingpreview|duckduckbot|baiduspider|yandex|applebot|facebookexternalhit|facebot|ia_archiver|embedly|quora link preview|pinterest|vkshare|w3c_validator|whatsapp|telegrambot|twitterbot|linkedinbot|petalbot|semrush|ahrefs|dotbot|rogerbot|lighthouse|page speed|gtmetrix|pingdom/i

function isBot(): boolean {
  if (typeof navigator === 'undefined') return false
  return BOT_UA_RE.test(navigator.userAgent || '')
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return m ? decodeURIComponent(m[1]) : null
}

function writeCookie(name: string, value: string): void {
  document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=${COOKIE_MAX_AGE};SameSite=Lax`
}

function mapBrowserLang(): string | null {
  const raw = typeof navigator !== 'undefined' ? navigator.language : ''
  if (!raw) return null
  const base = raw.split('-')[0]?.toLowerCase()
  return base || null
}

type Props = {
  defaultLocale: string
  currentLocale: string
  languages: PublicLanguageOption[]
}

/**
 * Modo `auto_redirect`: redireciona o VISITANTE (não bots) para a versão do site no idioma do
 * navegador, se houver tradução. Não renderiza UI. Regras (ver PLANO_AUTO_REDIRECT_IDIOMA.md):
 * exclui bots; respeita o cookie `lang_pref` (1ª visita só); só redireciona a partir da página do
 * idioma padrão; preserva o caminho; usa `location.replace` (não polui o histórico).
 */
export function LanguageAutoRedirect({ defaultLocale, currentLocale, languages }: Props): null {
  const pathname = usePathname() || '/'

  useEffect(() => {
    // Depende de navigator/document/cookie (só pós-hidratação): sincronização com sistema externo.
    if (isBot()) return
    if (readCookie(COOKIE_NAME)) return

    const defaultBase = defaultLocale.split('-')[0]?.toLowerCase()
    const currentBase = currentLocale.split('-')[0]?.toLowerCase()
    // Só redireciona a partir da página do idioma PADRÃO. Visitar /en, /es direto = intencional.
    if (currentBase !== defaultBase) return

    const browserBase = mapBrowserLang()
    if (!browserBase || browserBase === currentBase) return

    const match = languages.find(
      (l) =>
        l.code.toLowerCase() === browserBase || l.code.toLowerCase().startsWith(`${browserBase}-`),
    )
    if (!match || match.code === currentLocale) return

    // Memoriza a preferência (anti-loop) e redireciona para o MESMO caminho no idioma alvo.
    writeCookie(COOKIE_NAME, match.code)
    const target = buildLocalizedPath(stripPathForLocaleSwitch(pathname), match.code, defaultLocale)
    const search = typeof window !== 'undefined' ? window.location.search : ''
    window.location.replace(target + search)
  }, [defaultLocale, currentLocale, languages, pathname])

  return null
}
